import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, CascaderProps, Select, SelectProps, Space, TreeSelect} from 'antd';
import {useDispatch, useIntl, useSelector} from 'umi';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import styles from './index.less';
import classNames from 'classnames';
import {FILTER_PLACES} from '@/components/Filters/LeadFilters';
import {useLeadCardClick} from '@/components/Kanban/LeadCardContext';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {CheckAccess} from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import {PlusOutlined} from '@ant-design/icons';

const { Option } = Select;

type ILeadDropdownOption = {
  name?: string,
  label?: string,
  title?: string,
  value?: string,
  id?: string | number,
  color?: string,
  hashId?: string,
  key?: React.Key,
  children?: ILeadDropdownOption[],
  options?: ILeadDropdownOption[],
  [otherKey: string]: any
}
export type ILeadDropdownFilterProps = {
  options: ILeadDropdownOption[],
  className?: string,
  filterKey: string,
  fetchOptionsAction?: string,
  optionLabelKey?: string,
  optionValueKey?: string,
  placeholder?: any,
  filterMode?: string | null,
  optionFilterProp?: string | null,
  localSearch?: boolean,
  autoFocus?: boolean,
  value?: any,
  optionRender?: (option: ILeadDropdownOption) => React.ReactNode,
  nativeOptionRender?: (option: ILeadDropdownOption) => React.ReactNode,
  labelRender?: (option: ILeadDropdownOption) => React.ReactNode,
  onChange?: (value, option) => void
  processValueBeforeChange?: (value, option) => any
  onSearch?: (query) => void,
  filterOption?: (query) => boolean,
  selectProps?: Partial<SelectProps> | Partial<CascaderProps>
  loading?: boolean
  Wrapper?: any
  multiple?: boolean,
  transformOptionBasedOnValue?: (option, value) => any
  type?: 'dropdown' | 'tree'
  treeData?: ILeadDropdownOption[]
  treeSelectPlaceHolder?: string
  strongPlaceholder?: boolean
  valueStrictlyInOptions?: boolean
  treeProps?: {}
  onOpenChange?: (visible: boolean) => void
  onSelect?: (value, option) => void
  onCreate?: (searchValue: string) => void | Promise<any>
  showCreateWhenEmpty?: boolean
  createEntity?: keyof typeof INTL_ENTITY
  createAction?: keyof typeof INTL_ACTION
  createPermission?: any[]
}

const DEFAULT_ACTION = 'preferences/change';
// A filter place missing here silently drops every selection — handleChange
// only dispatches for places listed below.
const FILTER_ACTIONS = {
  [FILTER_PLACES.LEAD_LIST]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.TASKS_TABLE]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.LEAD_PERFORMANCE_REPORT]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.LEAD_FUNNEL_REPORT]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.LEAD_ROI_FUNNEL_REPORT]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.TEAM_VIEW_CALENDAR]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.LEAD_TEAM_PERFORMANCE]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.CALLS]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.LEAD_CALL_BACK_REPORT]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.LEAD_BOARD]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.APPOINTMENTS_TABLE]: { action: DEFAULT_ACTION },
  [FILTER_PLACES.MANAGER_OVERVIEW]: { action: DEFAULT_ACTION },
};

const LeadDropdownFilter = ({
                              options,
                              className,
                              filterKey,
                              onChange,
                              fetchOptionsAction,
                              optionLabelKey,
                              optionValueKey,
                              localSearch = true,
                              filterMode = FILTER_PLACES.LEAD_LIST,
                              onSearch,
                              optionFilterProp = 'label',
                              value,
                              autoFocus,
                              optionRender,
                              nativeOptionRender,
                              labelRender,
                              placeholder,
                              filterOption,
                              loading = false,
                              Wrapper = null,
                              transformOptionBasedOnValue,
                              processValueBeforeChange,
                              type = 'dropdown',
                              treeData = [],
                              onSelect,
                              treeProps,
                              valueStrictlyInOptions = false,
                              selectProps,
                              onOpenChange,
                              onCreate,
                              showCreateWhenEmpty = false,
                              createEntity,
                              createAction = INTL_ACTION.ADD as any,
                              createPermission,
                              ...restProps
                            }: ILeadDropdownFilterProps) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [creating, setCreating] = useState(false);
  const selectRef = useRef<any>();
  const dispatch = useDispatch();
  const intl = useIntl();
  const filters = useSelector((state: DefaultRootState) => PreferencesSelectors.selectFilters(state, filterMode));
  const { setShouldIgnoreClick } = useLeadCardClick();

  // --- memoized constants / handlers ------------------------------------
  const popUpClassName = useMemo(() => `lead-dropdown-${filterKey}`, [filterKey]);

  const handleDropdownVisibleChange = useCallback((visible: boolean) => {
    setShouldIgnoreClick(visible);
    if (!visible) {
      setSearchValue('');
    }
    onOpenChange?.(visible);
  }, [setShouldIgnoreClick, onOpenChange]);

  useEffect(() => {
    if (fetchOptionsAction) {
      dispatch({ type: fetchOptionsAction });
    }
    if (autoFocus) setOpen(true);
  }, [dispatch, fetchOptionsAction, autoFocus]);

  // Raw value from Redux or prop (no validation here)
  const rawValue = useMemo(() => {
    if (filterMode !== 'select') return filters?.[filterKey];
    return value;
  }, [filters, filterKey, filterMode, value]);

  // Helpers to derive option fields (stable)
  const getOptionValue = useCallback((option: ILeadDropdownOption) =>
      option[optionValueKey] ?? option.value ?? option.hashId ?? option.id
    , [optionValueKey]);

  const getOptionKey = useCallback((option: ILeadDropdownOption) =>
      option.key ?? getOptionValue(option)
    , [getOptionValue]);

  const getOptionLabel = useCallback((option: ILeadDropdownOption) =>
      optionLabelKey === null ? option : (option[optionLabelKey] ?? option.name ?? option.label ?? option.title)
    , [optionLabelKey]);

  // Build options tree (transforms applied with the CURRENT rawValue)
  const baseOptions = useMemo<ILeadDropdownOption[]>(
    () => (type === 'tree' ? treeData : (options ?? [])),
    [type, treeData, options]
  );

  const builtOptions = useMemo<ILeadDropdownOption[]>(() => {
    const build = (childrenOptions: ILeadDropdownOption[]): ILeadDropdownOption[] => {
      const safe = Array.isArray(childrenOptions) ? childrenOptions : [];
      return safe.map((original) => {
        let option = original;
        if (transformOptionBasedOnValue) option = transformOptionBasedOnValue(option, rawValue);
        const next: ILeadDropdownOption = {
          ...option,
          key: getOptionKey(option),
          label: getOptionLabel(option),
          value: getOptionValue(option),
        };
        if (option.children) next.children = build(option.children);
        if (option.options) next.options = build(option.options);
        return next;
      });
    };
    return build(baseOptions);
  }, [baseOptions, transformOptionBasedOnValue, rawValue, getOptionKey, getOptionLabel, getOptionValue]);

  // Flattened options list (for validation)
  const flatOptions = useMemo<ILeadDropdownOption[]>(() => {
    const out: ILeadDropdownOption[] = [];
    const traverse = (opts: ILeadDropdownOption[]) => {
      for (const o of opts) {
        out.push(o);
        if (o.children) traverse(o.children);
        if (o.options) traverse(o.options);
      }
    };
    traverse(builtOptions);
    return out;
  }, [builtOptions]);

  // Validate value against options if needed
  const currentValue = useMemo(() => {
    if (!valueStrictlyInOptions) return rawValue;
    const isAllowed = (v) => flatOptions.some(o => o.value === v);
    if (Array.isArray(rawValue)) return rawValue.filter(isAllowed);
    return isAllowed(rawValue) ? rawValue : undefined;
  }, [rawValue, valueStrictlyInOptions, flatOptions]);

  const handleChange = useCallback((val, option, extra) => {
    let nextVal = (val === undefined ? null : val);
    if (processValueBeforeChange) nextVal = processValueBeforeChange(nextVal, option);

    const reduxAction = FILTER_ACTIONS[filterMode];
    if (reduxAction) {
      dispatch({
        type: reduxAction.action,
        payload: { [filterKey]: nextVal, place: filterMode },
      });
    }
    if (extra && extra.triggerValue && extra.checked) {
      onSelect?.(extra.triggerValue, option);
    }
    onChange?.(nextVal, option);
  }, [dispatch, filterKey, filterMode, processValueBeforeChange, onChange, onSelect]);

  const handleSearch = useCallback((query) => {
    setSearchValue(query);
    if (onSearch) onSearch(query);
  }, [onSearch]);

  // Tree utils (memoized maps for quick lookup)
  const treeValueToNode = useMemo(() => {
    if (type !== 'tree') return new Map<any, ILeadDropdownOption>();
    const map = new Map<any, ILeadDropdownOption>();
    const traverse = (nodes: ILeadDropdownOption[]) => {
      for (const n of nodes) {
        map.set(n.value, n);
        if (n.children) traverse(n.children);
      }
    };
    traverse(builtOptions);
    return map;
  }, [type, builtOptions]);

  const handleSelectOnly = useCallback((val) => {
    const nodeItem = treeValueToNode.get(val);
    const values = nodeItem?.children?.length
      ? nodeItem.children.map(child => child.value)
      : [val];
    handleChange(values, null as any, undefined);
    onSelect?.(val, null);
  }, [treeValueToNode, handleChange, onSelect]);

  // AntD Select props + children (memoized)
  const memoSelectProps = useMemo<Partial<SelectProps>>(() => {
    const p: Partial<SelectProps> = {};

    if (!onSearch) {
      p.optionFilterProp = optionFilterProp || 'label';
    } else {
      p.filterOption = false;
    }

    if (filterOption) p.filterOption = filterOption;

    if (nativeOptionRender) {
      // @ts-ignore
      p.optionRender = nativeOptionRender;
      // @ts-ignore
      p.labelRender = (option) => nativeOptionRender(option, { isLabel: true });
    }

    if (!optionRender) {
      // Use options API
      // @ts-ignore
      p.options = builtOptions;
    }

    if (labelRender) {
      // @ts-ignore
      p.labelRender = (option) => labelRender(option, { isLabel: true });
    }

    if (autoFocus) {
      p.autoFocus = true;
      // @ts-ignore
      p.open = open;
      p.onDropdownVisibleChange = (visible) => {
        handleDropdownVisibleChange(visible);
        if (open) setOpen(visible);
      };
    }

    if (onCreate && createEntity) {
      const existingDropdownRender = selectProps?.dropdownRender;
      p.dropdownRender = (menu) => {
        const showCreateButton =
          (showCreateWhenEmpty || searchValue) &&
          !builtOptions.some(
            (o) =>
              (getOptionLabel(o) || '').toLowerCase() ===
              (searchValue || '').toLowerCase(),
          );

        return (
          <div>
            {existingDropdownRender ? existingDropdownRender(menu) : menu}
            {showCreateButton && (
              <CheckAccess
                access={
                  createPermission || [
                    PERMISSIONS.BUSINESS_PARTNER,
                  ]
                }
              >
                <div style={{ padding: '8px' }}>
                  <Button
                    block
                    icon={<PlusOutlined/>}
                    color={"primary"}
                    variant={"outlined"}
                    loading={creating}
                    onClick={async () => {
                      setCreating(true);
                      try {
                        await onCreate(searchValue);
                      } finally {
                        setCreating(false);
                      }
                    }}
                  >
                    <Space>
                      <F entity={createEntity} action={createAction} />
                      {searchValue && <span>"{searchValue}"</span>}
                    </Space>
                  </Button>
                </div>
              </CheckAccess>
            )}
          </div>
        );
      };
    }

    return p;
  }, [
    onSearch,
    optionFilterProp,
    filterOption,
    nativeOptionRender,
    optionRender,
    builtOptions,
    autoFocus,
    open,
    handleDropdownVisibleChange,
    onCreate,
    showCreateWhenEmpty,
    createEntity,
    searchValue,
    selectProps?.dropdownRender,
    createPermission,
    createAction,
    creating,
    getOptionLabel,
  ]);

  const renderedChildren = useMemo(() => {
    if (!optionRender) return null;
    return builtOptions.map(option => {
      // Check if this is an option group (has nested options)
      if (option.options && Array.isArray(option.options)) {
        return (
          <Select.OptGroup key={getOptionKey(option)} label={option.label}>
            {option.options.map(childOption => (
              <Option
                value={getOptionValue(childOption)}
                key={getOptionKey(childOption)}
                meta={childOption}
                label={getOptionLabel(childOption)}
              >
                {optionRender(childOption)}
              </Option>
            ))}
          </Select.OptGroup>
        );
      }
      // Regular option
      return (
        <Option
          value={getOptionValue(option)}
          key={getOptionKey(option)}
          meta={option}
          label={getOptionLabel(option)}
        >
          {optionRender(option)}
        </Option>
      );
    });
  }, [optionRender, builtOptions, getOptionValue, getOptionKey, getOptionLabel]);

  // --- Render branches ----------------------------------------------------
  if (type === 'tree') {
    const selectedValues: any[] = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);
    const emptySelection = !selectedValues || selectedValues.length === 0;

    const firstLabel = useMemo(() => {
      if (selectedValues.length === 0) return 'Select';
      const node = treeValueToNode.get(selectedValues[0]);
      return node?.title ?? 'Select';
    }, [selectedValues, treeValueToNode]);

    const remainingCount = selectedValues.length > 1 ? selectedValues.length - 1 : 0;

    return (
      <TreeSelect
        showSearch
        allowClear
        treeNodeFilterProp="title"
        popupMatchSelectWidth={false}
        classNames={{
          popup: {
            root: classNames(popUpClassName, {
              [styles.treeNoChildren]: !builtOptions.some(node => node.children && node.children.length > 0),
            }),
          },
        }}
        className={classNames(
          styles.selectContainer,
          className,
          { [styles.selectStrongPlaceholder]: (restProps as any)?.strongPlaceholder && emptySelection }
        )}
        treeDefaultExpandAll
        onChange={handleChange}
        // Use the ALREADY built tree options
        treeData={builtOptions as any}
        treeCheckable
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        placeholder={placeholder}
        style={{ minWidth: 120 }}
        value={selectedValues}
        maxTagCount={0}
        maxTagPlaceholder={() =>
          remainingCount > 0
            ? `${firstLabel} + ${remainingCount} ${intl.formatMessage({
              id: 'pages.leads.filters.more',
              defaultMessage: 'more',
            })}`
            : firstLabel
        }
        treeTitleRender={(node) => (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
            <span>{optionRender ? optionRender(node) : node.title}</span>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              className="tree-node-only-button"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectOnly(node.value);
              }}
            >
              <F id="pages.common.onlyFilter" defaultMessage="Only" />
            </Button>
          </div>
        )}
        onOpenChange={handleDropdownVisibleChange}
        {...(selectProps || {})}
        {...(treeProps || {})}
      />
    );
  }

  const emptySelectionSelect = !currentValue || (Array.isArray(currentValue) && currentValue.length === 0);

  return (
    <Select
      onClick={(e) => e.stopPropagation()}
      showSearch
      placeholder={placeholder}
      allowClear
      ref={selectRef}
      value={currentValue}
      onChange={handleChange}
      onSearch={handleSearch}
      onOpenChange={handleDropdownVisibleChange}
      loading={loading}
      virtual
      popupMatchSelectWidth={false}
      classNames={{ popup: { root: popUpClassName } }}
      className={classNames(
        styles.selectContainer,
        className,
        { [styles.selectStrongPlaceholder]: (restProps as any)?.strongPlaceholder && emptySelectionSelect }
      )}
      {...(selectProps || {})}
      {...memoSelectProps}
    >
      {renderedChildren}
    </Select>
  );
};

export default React.memo(LeadDropdownFilter);
